import React, { useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'

// styles
import * as SC from './NpForBusinessSectionStyles'
import { HomePageContainer } from '../../HomePageStyles'
import { BREAKPOINTS_INTS } from '../../../../styles/constants'

// types
import { CmsHomepageData } from '../../../../types/types'

// utils
import { pushToDataLayer } from '../../../../utils/dataLayer'
import { getPromotionEvent } from '../../../../utils/dataLayerEvents'
import { getLinkAttributes } from '../../../../utils/helper'
import { AppContext } from '../../../../utils/appProvider'

type Props = {
	cmsData: CmsHomepageData['npForBusinessData']
}

// component
const NpForBusiness = (props: Props) => {
	const { cmsData } = props
	const { image, backgroundImage, backgroundImageWebP } = cmsData

	const { productionEnvironment } = useContext(AppContext)
	const { locale } = useIntl()

	const sectionRef = useRef<HTMLDivElement>(null)

	// Tracks promo banner view event using IntersectionObserver. Sends the event once when the banner is 50% visible.
	useEffect(() => {
		const section = sectionRef.current // Copy ref value

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
					pushToDataLayer(
						getPromotionEvent('view_promotion', [
							{
								code: image.trackingID || 'B2B_registration',
								location: 'salons_bottom',
								position: 1,
								destination_url: image.link || 'unclickable'
							}
						])
					)
				}
			},
			{
				threshold: 0.5
			}
		)

		if (section) {
			observer.observe(section)
		}

		return () => {
			if (section) {
				observer.unobserve(section)
			}
		}
	}, [image.trackingID, image.link])

	const picture = (
		<SC.Picture>
			{/* WebP variants */}
			{image.desktopWebP && (
				<source srcSet={image.desktopWebP} type='image/webp' media={`(min-width: ${BREAKPOINTS_INTS.md}px)`} width={1280} height={460} />
			)}
			{image.mobileWebP && (
				<source srcSet={image.mobileWebP} type='image/webp' media={`(max-width: ${BREAKPOINTS_INTS.md - 1}px)`} width={772} height={332} />
			)}

			{/* JPEG fallback */}
			<source srcSet={image.desktop} media={`(min-width: ${BREAKPOINTS_INTS.md}px)`} width={1280} height={460} />
			<source srcSet={image.mobile} media={`(max-width: ${BREAKPOINTS_INTS.md - 1}px)`} width={772} height={332} />

			{/* Fallback <img> tag for maximum compatibility */}
			<img src={image.desktop} alt={image.alt} loading='lazy' width={1280} height={460} />
		</SC.Picture>
	)

	return (
		<SC.Section $backgroundImage={backgroundImage} $backgroundImageWebP={backgroundImageWebP} ref={sectionRef}>
			<HomePageContainer>
				{image.link ? (
					<SC.Link
						href={image.link}
						{...getLinkAttributes(image.link, productionEnvironment, locale)}
						onClick={() =>
							pushToDataLayer(
								getPromotionEvent('select_promotion', [
									{
										code: image.trackingID || 'B2B_registration',
										location: 'salons_bottom',
										position: 1,
										destination_url: image.link || 'unclickable'
									}
								])
							)
						}
					>
						{picture}
					</SC.Link>
				) : (
					picture
				)}
			</HomePageContainer>
		</SC.Section>
	)
}

export default NpForBusiness
